/** Buchung/Vertrag (Verwaltung): Status, Zustimmung, PDF, Perioden, Kaution, Verlängern, Kündigen, Stornieren. */
import { useState } from "react";
import { Link, useParams } from "react-router";
import { Button, Dialog, Notice, Seitenzustand, StatusBadge, TextField } from "../ui";
import { Card, Dl, PageHeader, Select, Split, Textarea } from "../ui/Layout";
import { ApiFehler } from "../api/client";
import { formatEuro } from "@shared/geld";
import { formatDatumZeit, formatTag, tagVon } from "@shared/zeit";
import { useBuchung, useBuchungAktion, vertragPdfUrl, type BuchungDetail as Detail } from "./buchungen-api";

export const BUCHUNG_STATUS: Record<Detail["buchung"]["status"], { badge: "wait" | "paid" | "neutral" | "attention"; text: string }> = {
  entwurf: { badge: "neutral", text: "Entwurf" },
  reserviert: { badge: "wait", text: "Reserviert, Vertrag offen" },
  aktiv: { badge: "paid", text: "Gebucht" },
  beendet: { badge: "neutral", text: "Beendet" },
  storniert: { badge: "neutral", text: "Storniert" },
};

export function einheitText(e: "woche" | "monat", n = 1): string {
  return e === "woche" ? (n === 1 ? "Woche" : "Wochen") : n === 1 ? "Monat" : "Monate";
}

export function BuchungDetail() {
  const { id = "" } = useParams();
  const q = useBuchung(id);
  const aktion = useBuchungAktion();
  const [dialog, setDialog] = useState<null | "verlaengern" | "kuendigen" | "stornieren" | "kaution">(null);
  const [form, setForm] = useState({ dauer: "1", zum: "", notiz: "", grund: "", art: "bar" as "bar" | "ueberweisung", am: tagVon() });
  const [fehler, setFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);

  async function ausfuehren(a: Parameters<typeof aktion.mutateAsync>[0]["aktion"], daten?: Record<string, unknown>, text?: string) {
    setFehler(null);
    try {
      await aktion.mutateAsync({ id, aktion: a, daten });
      setDialog(null);
      if (text) setMeldung(text);
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : "Das hat nicht geklappt.");
    }
  }

  return (
    <Seitenzustand query={q} lines={8}>
      {(d) => {
        const b = d.buchung;
        const st = BUCHUNG_STATUS[b.status];
        const laeuftOderKommt = ["reserviert", "aktiv"].includes(b.status);
        return (
          <>
            <PageHeader
              kicker={<Link to={`/laden/mieter/${d.mieter.id}`}>{d.mieter.name}</Link>}
              title={`Buchung ${b.nummer}`}
              sub={
                <span className="sc-toolbar">
                  <StatusBadge status={st.badge}>{st.text}</StatusBadge>
                  <span>Regal {d.regal?.nummer} · {formatTag(b.start)}–{b.ende ? formatTag(b.ende) : "offen (Abo)"}{b.gekuendigtZum ? ` · gekündigt zum ${formatTag(b.gekuendigtZum)}` : ""}</span>
                </span>
              }
              actions={
                <>
                  <a href={vertragPdfUrl(b.id)} target="_blank" rel="noreferrer" className="rg-btn rg-btn--secondary">Vertrag (PDF)</a>
                  {b.status === "aktiv" && b.art === "fest" && b.ende && b.ende >= tagVon() ? <Button variant="primary" icon="calendar-days" onClick={() => setDialog("verlaengern")}>Verlängern</Button> : null}
                </>
              }
            />
            {meldung ? <Notice tone="success">{meldung}</Notice> : null}
            {fehler && !dialog ? <Notice tone="error">{fehler}</Notice> : null}
            {b.status === "reserviert" ? (
              <Notice
                tone={d.zustimmungUeberfaellig ? "attention" : "info"}
                title={d.zustimmungUeberfaellig ? `Zustimmung überfällig seit ${b.reserviertBis ? formatDatumZeit(b.reserviertBis) : "–"}` : `Vertrag wartet auf Zustimmung${b.reserviertBis ? ` · reserviert bis ${formatDatumZeit(b.reserviertBis)}` : ""}`}
                action={
                  <span className="sc-toolbar">
                    {d.mieter.email ? <Button variant="secondary" icon="mail" loading={aktion.isPending} onClick={() => void ausfuehren("link", undefined, "Link erneut gesendet.")}>Link erneut senden</Button> : null}
                    <Button variant="primary" icon="check" loading={aktion.isPending} onClick={() => confirm("Vor Ort zugestimmt? Das unterschriebene Papier bitte abheften.") && void ausfuehren("zustimmung-vor-ort", undefined, "Zustimmung vor Ort eingetragen.")}>Vor Ort zugestimmt</Button>
                  </span>
                }
              >
                {d.mieter.email ? `${d.mieter.name} bekommt den Vertrag per E-Mail und stimmt im Mieterportal zu. ` : `${d.mieter.name} hat keine E-Mail – Zustimmung nur vor Ort mit Papier. `}
                Das Regal bleibt bis zur Entscheidung belegt; es wird nicht automatisch storniert.
              </Notice>
            ) : null}
            <Split
              main={
                <>
                  <Card title="Vertrag">
                    <Dl
                      rows={[
                        ["Mieter", <Link key="m" to={`/laden/mieter/${d.mieter.id}`}>{d.mieter.name} · Nr. {d.mieter.mieternummer}</Link>],
                        ["Regal", d.regal ? <Link key="r" to={`/laden/regale/${d.regal.id}`}>{d.regal.nummer}{d.regal.lage ? ` · ${d.regal.lage}` : ""}</Link> : "–"],
                        ["Art", b.art === "abo" ? `Miet-Abo je ${einheitText(b.einheit)}, Kündigungsfrist ${b.kuendigungsfristWochen} Wochen zum Monatsende` : `Fester Zeitraum, ${d.perioden.length} ${einheitText(b.einheit, d.perioden.length)}`],
                        ["Zeitraum", `${formatTag(b.start)} bis ${b.ende ? formatTag(b.ende) : "offen"}`],
                        ["Miete", `${formatEuro(b.mieteCent)} je ${einheitText(b.einheit)}`],
                        ["Provision", `${b.provisionProzent} % auf Verkäufe`],
                        ["Kaution", b.kautionCent ? formatEuro(b.kautionCent) : "keine"],
                        ["Zustimmung", b.zustimmungAt ? `${formatDatumZeit(b.zustimmungAt)} · ${b.zustimmungArt === "portal" ? "im Mieterportal" : "vor Ort (Papier)"}` : "noch offen"],
                        ["Version", `${b.vertragVersion ?? "–"} · Prüfsumme ${(b.vertragTextHash ?? "").slice(0, 16).replace(/(.{4})(?=.)/g, "$1 ")}`],
                        ["Notiz", b.notiz || "–"],
                      ]}
                    />
                  </Card>
                  <Card title="Mietperioden" flush>
                    <div className="app-tabellen-scroll">
                      <table className="rg-table">
                        <thead><tr><th>Von</th><th>Bis</th><th>Betrag</th><th>Status</th></tr></thead>
                        <tbody>
                          {d.perioden.length === 0 ? <tr><td colSpan={4} className="rg-table__empty">Keine Perioden.</td></tr> : null}
                          {d.perioden.map((p) => (
                            <tr key={p.id}>
                              <td>{formatTag(p.von)}</td>
                              <td>{formatTag(p.bis)}</td>
                              <td className="sc-num">{formatEuro(p.betragCent)}</td>
                              <td><StatusBadge status={p.status === "bezahlt" ? "paid" : p.status === "verrechnet" ? "neutral" : "wait"}>{p.status === "offen" ? "Offen" : p.status === "verrechnet" ? "Verrechnet" : "Bezahlt"}</StatusBadge></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {b.art === "abo" && laeuftOderKommt ? <p className="sc-muted sc-small" style={{ padding: "var(--space-3) var(--space-4)" }}>Weitere Perioden entstehen automatisch, solange das Abo läuft. Mietrechnungen und Verrechnung kommen mit Etappe 6.</p> : null}
                  </Card>
                </>
              }
              aside={
                <>
                  <Card title="Kaution">
                    {!d.kaution ? (
                      <p className="sc-muted">Keine Kaution vereinbart.</p>
                    ) : d.kaution.erhaltenAt ? (
                      <Dl rows={[["Betrag", formatEuro(d.kaution.betragCent)], ["Erhalten", `${formatTag(d.kaution.erhaltenAt)} · ${d.kaution.erhaltenArt === "bar" ? "bar" : "Überweisung"}`]]} />
                    ) : (
                      <div className="sc-col">
                        <span><span className="sc-strong">{formatEuro(d.kaution.betragCent)}</span> <StatusBadge status="wait">Noch nicht erhalten</StatusBadge></span>
                        <Button variant="secondary" icon="banknote" onClick={() => setDialog("kaution")}>Als erhalten erfassen</Button>
                      </div>
                    )}
                  </Card>
                  {laeuftOderKommt ? (
                    <Card title="Buchung verwalten">
                      <div className="sc-col">
                        {b.art === "abo" && b.status === "aktiv" && !b.gekuendigtAt ? <Button variant="secondary" icon="x" block onClick={() => { setForm({ ...form, zum: d.fruehesteKuendigung ?? "" }); setDialog("kuendigen"); }}>Abo kündigen</Button> : null}
                        {b.art === "fest" && b.status === "aktiv" && b.start <= tagVon() ? <Button variant="secondary" icon="x" block onClick={() => { setForm({ ...form, zum: "" }); setDialog("kuendigen"); }}>Vorzeitig beenden</Button> : null}
                        {b.status === "reserviert" || b.start > tagVon() ? <Button variant="quiet" icon="trash-2" block onClick={() => setDialog("stornieren")}>Stornieren</Button> : null}
                        <p className="sc-muted sc-small">Gekündigt wird zum Ende einer Periode; angebrochene Perioden werden anteilig berechnet.</p>
                      </div>
                    </Card>
                  ) : null}
                </>
              }
            />

            <Dialog open={dialog === "verlaengern"} title="Buchung verlängern" size="sm" onClose={() => setDialog(null)} actions={<><Button variant="quiet" onClick={() => setDialog(null)}>Abbrechen</Button><Button variant="primary" loading={aktion.isPending} onClick={() => void ausfuehren("verlaengern", { dauer: Number(form.dauer) }, "Buchung verlängert.")}>Verlängern</Button></>}>
              <div className="app-form">
                {fehler ? <Notice tone="error">{fehler}</Notice> : null}
                <TextField label={`Verlängern um (${einheitText(b.einheit, 2)})`} value={form.dauer} onChange={(e) => setForm({ ...form, dauer: e.target.value })} inputMode="numeric" hint={`Bisheriges Ende ${b.ende ? formatTag(b.ende) : "–"} · Miete ${formatEuro(b.mieteCent)} je ${einheitText(b.einheit)}`} autoFocus />
              </div>
            </Dialog>
            <Dialog open={dialog === "kuendigen"} title={b.art === "abo" ? "Abo kündigen" : "Vorzeitig beenden"} size="sm" onClose={() => setDialog(null)} actions={<><Button variant="quiet" onClick={() => setDialog(null)}>Abbrechen</Button><Button variant="primary" loading={aktion.isPending} onClick={() => void ausfuehren("kuendigen", { zum: form.zum, notiz: form.notiz }, "Ende eingetragen.")}>Bestätigen</Button></>}>
              <div className="app-form">
                {fehler ? <Notice tone="error">{fehler}</Notice> : null}
                <TextField label="Letzter Miettag" type="date" value={form.zum} onChange={(e) => setForm({ ...form, zum: e.target.value })} hint={b.art === "abo" ? `Frühestmöglich ${d.fruehesteKuendigung ? formatTag(d.fruehesteKuendigung) : "–"} (Frist ${b.kuendigungsfristWochen} Wochen zum Monatsende)` : "Muss vor dem bisherigen Ende liegen"} />
                <Textarea label="Notiz" value={form.notiz} onChange={(e) => setForm({ ...form, notiz: e.target.value })} rows={2} />
              </div>
            </Dialog>
            <Dialog open={dialog === "stornieren"} title="Buchung stornieren" size="sm" onClose={() => setDialog(null)} actions={<><Button variant="quiet" onClick={() => setDialog(null)}>Abbrechen</Button><Button variant="danger" loading={aktion.isPending} onClick={() => void ausfuehren("stornieren", { grund: form.grund }, "Buchung storniert.")}>Stornieren</Button></>}>
              <div className="app-form">
                {fehler ? <Notice tone="error">{fehler}</Notice> : null}
                <p>Das Regal wird wieder frei. Der Vertrag bleibt mit Status „storniert“ nachvollziehbar.</p>
                <TextField label="Grund" value={form.grund} onChange={(e) => setForm({ ...form, grund: e.target.value })} />
              </div>
            </Dialog>
            <Dialog open={dialog === "kaution"} title="Kaution erhalten" size="sm" onClose={() => setDialog(null)} actions={<><Button variant="quiet" onClick={() => setDialog(null)}>Abbrechen</Button><Button variant="primary" loading={aktion.isPending} onClick={() => void ausfuehren("kaution", { art: form.art, am: form.am }, "Kaution als erhalten erfasst.")}>Erfassen</Button></>}>
              <div className="app-form">
                {fehler ? <Notice tone="error">{fehler}</Notice> : null}
                <Select label="Art" value={form.art} onChange={(e) => setForm({ ...form, art: e.target.value as "bar" | "ueberweisung" })} hint="Bar landet als Einlage im Kassenbuch">
                  <option value="bar">Bar im Laden</option>
                  <option value="ueberweisung">Überweisung</option>
                </Select>
                <TextField label="Erhalten am" type="date" value={form.am} onChange={(e) => setForm({ ...form, am: e.target.value })} />
              </div>
            </Dialog>
          </>
        );
      }}
    </Seitenzustand>
  );
}
